import { useEffect, useState, type FormEvent } from 'react';
import { api, describeError, useOp } from '../api.ts';
import { Link, useRouter } from '../router.tsx';
import type { MerchantFacts, MerchantLinks, MerchantSiteRow, MerchantWorkspace as Workspace, SwitchPreview, SwitchResult } from '../types.ts';
import { runWrite, useChanges } from '../ui/changes.ts';
import { ErrorBox, Loading, Modal, StateBadge, formatDate } from '../ui/kit.tsx';
import { useToast } from '../ui/toasts.tsx';
import { MerchantCatalogPanels } from './catalog/routes.tsx';

const TEXT_FACTS: [keyof MerchantFacts, string][] = [
  ['shipping', 'Shipping'],
  ['freeShippingThreshold', 'Free delivery threshold'],
  ['returns', 'Returns'],
  ['loyaltyProgram', 'Loyalty programme'],
  ['studentDiscount', 'Student discount'],
  ['customerService', 'Customer service'],
];
const LIST_FACTS: [keyof MerchantFacts, string][] = [
  ['paymentMethods', 'Payment methods'],
  ['deliveryOptions', 'Delivery options'],
  ['otherDiscounts', 'Other discounts'],
  ['salePatterns', 'Sale patterns'],
];

export function MerchantWorkspace({ merchantRef }: { merchantRef: string }) {
  const changes = useChanges();
  const workspace = useOp<Workspace>('admin.merchants.get', { ref: merchantRef }, changes);
  const links = useOp<MerchantLinks>('admin.links.forMerchant', { merchantRef }, changes);
  const data = workspace.data;

  return (
    <section className="page">
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/merchants">Merchants</Link> <span aria-hidden="true">/</span> <span>{data?.name ?? '…'}</span>
      </nav>
      {workspace.error ? <ErrorBox error={workspace.error} onRetry={workspace.reload} /> : null}
      {!data && workspace.loading ? <Loading /> : null}
      {data ? (
        <>
          <header className="page-head">
            <div>
              <h1>
                {data.name} <span className="muted small">{data.market}</span>
              </h1>
              <p className="muted">
                {data.masterMerchant.parentName ? `${data.masterMerchant.parentName} › ` : ''}
                {data.masterMerchant.name} · {data.categories.join(', ') || 'no category'}
                {data.homepageUrl ? (
                  <>
                    {' · '}
                    <a href={data.homepageUrl} target="_blank" rel="noreferrer noopener">
                      {data.homepageUrl.replace(/^https?:\/\//, '')}
                    </a>
                  </>
                ) : null}
              </p>
            </div>
            <Link to={`/coupons/new?merchant=${data.ref}`} className="btn btn-primary">
              New coupon
            </Link>
          </header>
          <div className="workspace-grid">
            <SiteMatrix merchantRef={data.ref} sites={data.sites} />
            <ProgramsPanel merchantRef={data.ref} links={links} />
            <MerchantCoupons workspace={data} />
            <FactsForm workspace={data} />
            <MerchantCatalogPanels workspace={data} />
          </div>
        </>
      ) : null}
    </section>
  );
}

function SiteMatrix({ merchantRef, sites }: { merchantRef: string; sites: MerchantSiteRow[] }) {
  const toast = useToast();
  const update = (site: MerchantSiteRow, patch: Record<string, unknown>, label: string) =>
    runWrite(toast, 'admin.merchants.updateSite', { ref: merchantRef, propertyRef: site.propertyRef, ...patch }, `${site.propertyName}: ${label}`);

  return (
    <article className="card span-2" aria-labelledby="matrix-title">
      <h2 id="matrix-title">On your sites</h2>
      <table className="table compact">
        <thead>
          <tr>
            <th scope="col">Site</th>
            <th scope="col">Shown</th>
            <th scope="col">Indexable</th>
            <th scope="col">Featured</th>
            <th scope="col" className="num">Sort weight</th>
            <th scope="col">SEO text</th>
            <th scope="col" className="num">Live</th>
            <th scope="col" className="num">In review</th>
          </tr>
        </thead>
        <tbody>
          {sites.map((site) => (
            <tr key={site.propertyRef}>
              <th scope="row">
                {site.propertyName}
                <small className="muted block">
                  {site.brandName}
                  {site.pageUrl && site.shown ? (
                    <>
                      {' · '}
                      <a href={site.pageUrl} target="_blank" rel="noreferrer noopener" title={site.pageUrl}>
                        View on site
                      </a>
                    </>
                  ) : null}
                </small>
              </th>
              <td>
                <input
                  type="checkbox"
                  aria-label={`Shown on ${site.propertyName}`}
                  checked={site.shown}
                  disabled={!site.canEdit}
                  onChange={(e) => update(site, { shown: e.target.checked }, e.target.checked ? 'shown' : 'hidden')}
                />
              </td>
              <td>
                <select
                  aria-label={`Indexable on ${site.propertyName}`}
                  value={site.indexable === null ? 'auto' : site.indexable ? 'yes' : 'no'}
                  disabled={!site.canEdit}
                  onChange={(e) => update(site, { indexable: e.target.value === 'auto' ? null : e.target.value === 'yes' }, `indexable ${e.target.value}`)}
                >
                  <option value="auto">Auto (thin-page rule)</option>
                  <option value="yes">Index</option>
                  <option value="no">Noindex</option>
                </select>
              </td>
              <td>
                <input
                  type="checkbox"
                  aria-label={`Featured on ${site.propertyName}`}
                  checked={site.featured}
                  disabled={!site.canEdit}
                  onChange={(e) => update(site, { featured: e.target.checked }, e.target.checked ? 'featured' : 'not featured')}
                />
              </td>
              <td className="num">
                <SortWeight site={site} onCommit={(value) => update(site, { sortWeight: value }, `sort weight ${value}`)} />
              </td>
              <td>
                {site.seoTextBlocks > 0 ? `${site.seoTextBlocks} blocks` : <span className="muted">generated</span>}
                {site.hasMetaTitle ? <span className="badge badge-muted">meta</span> : null}
              </td>
              <td className={`num ${site.shown && site.liveCoupons === 0 ? 'text-warn' : ''}`}>{site.liveCoupons}</td>
              <td className="num">{site.pending || '–'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </article>
  );
}

function SortWeight({ site, onCommit }: { site: MerchantSiteRow; onCommit: (value: number) => void }) {
  const [value, setValue] = useState(String(site.sortWeight));
  useEffect(() => setValue(String(site.sortWeight)), [site.sortWeight]);
  const commit = () => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed !== site.sortWeight) onCommit(parsed);
    else setValue(String(site.sortWeight));
  };
  return (
    <input
      type="number"
      className="input-narrow"
      aria-label={`Sort weight on ${site.propertyName}`}
      value={value}
      disabled={!site.canEdit}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

function FactsForm({ workspace }: { workspace: Workspace }) {
  const toast = useToast();
  const initial = () => ({
    description: workspace.description ?? '',
    status: workspace.status,
    text: Object.fromEntries(TEXT_FACTS.map(([key]) => [key, (workspace.facts[key] as string | undefined) ?? ''])),
    lists: Object.fromEntries(LIST_FACTS.map(([key]) => [key, ((workspace.facts[key] as string[] | undefined) ?? []).join('\n')])),
  });
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(initial()), [workspace]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const facts: Record<string, unknown> = { ...form.text };
    for (const [key] of LIST_FACTS) facts[key] = form.lists[key]!.split('\n').map((s) => s.trim()).filter(Boolean);
    await runWrite<{ changed: string[] }>(toast, 'admin.merchants.updateFacts', { ref: workspace.ref, description: form.description, status: form.status, facts }, (r) =>
      r.changed.length ? `Saved ${r.changed.length} change${r.changed.length === 1 ? '' : 's'}` : 'Nothing changed',
    );
    setSaving(false);
  };

  const disabled = !workspace.canEditFacts;
  return (
    <form className="card" onSubmit={submit} aria-labelledby="facts-title">
      <h2 id="facts-title">Shared facts ({workspace.market})</h2>
      <p className="muted small" data-testid="merchant-sharing">
        Shared across {workspace.sharing.sites} site{workspace.sharing.sites === 1 ? '' : 's'} in {workspace.sharing.organizations} organization{workspace.sharing.organizations === 1 ? '' : 's'}.
        {disabled && workspace.sharing.outsideYourAccess > 0 ? ` ${workspace.sharing.outsideYourAccess} of them are outside your access, so only a catalog owner can change these.` : ''} Each site has its own description in Page content.
      </p>
      <div className="form-grid">
        <label className="span-2">
          Default description for sites added later
          <textarea rows={3} value={form.description} disabled={disabled} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </label>
        {TEXT_FACTS.map(([key, label]) => (
          <label key={key}>
            {label}
            <input value={form.text[key]} disabled={disabled} onChange={(e) => setForm({ ...form, text: { ...form.text, [key]: e.target.value } })} />
          </label>
        ))}
        {LIST_FACTS.map(([key, label]) => (
          <label key={key}>
            {label} <small className="muted">one per line</small>
            <textarea rows={2} value={form.lists[key]} disabled={disabled} onChange={(e) => setForm({ ...form, lists: { ...form.lists, [key]: e.target.value } })} />
          </label>
        ))}
        <label>
          Status
          <select value={form.status} disabled={disabled} onChange={(e) => setForm({ ...form, status: e.target.value as 'active' | 'inactive' })}>
            <option value="active">Active</option>
            <option value="inactive">Inactive (hidden everywhere)</option>
          </select>
        </label>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={disabled || saving}>
          {saving ? 'Saving…' : 'Save facts'}
        </button>
        <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setForm(initial())}>
          Reset
        </button>
      </div>
    </form>
  );
}

const ACTION_LABELS: Record<SwitchPreview['lines'][number]['action'], string> = {
  already_active: 'Already active',
  reactivate: 'Re-activate existing link',
  generate: 'Generate new link',
  cannot_generate: 'Cannot generate — keeps current link',
};

function ProgramsPanel({ merchantRef, links }: { merchantRef: string; links: ReturnType<typeof useOp<MerchantLinks>> }) {
  const toast = useToast();
  const [preview, setPreview] = useState<{ programRef: string; data: SwitchPreview | null; error: string | null } | null>(null);
  const [switching, setSwitching] = useState(false);

  const openPreview = async (programRef: string) => {
    setPreview({ programRef, data: null, error: null });
    try {
      const data = await api.op<SwitchPreview>('admin.links.previewSwitch', { merchantRef, programRef });
      setPreview({ programRef, data, error: null });
    } catch (error) {
      setPreview({ programRef, data: null, error: describeError(error) });
    }
  };

  const confirm = async () => {
    if (!preview) return;
    setSwitching(true);
    const result = await runWrite<SwitchResult>(toast, 'admin.links.switchNetwork', { merchantRef, programRef: preview.programRef }, (r) =>
      `Switched: ${r.switched} re-activated, ${r.generated} generated${r.failed ? `, ${r.failed} kept their old link` : ''}`,
    );
    setSwitching(false);
    if (result) setPreview(null);
  };

  return (
    <article className="card span-2" aria-labelledby="programs-title">
      <h2 id="programs-title">Programs and links</h2>
      {links.error ? <ErrorBox error={links.error} onRetry={links.reload} /> : null}
      {links.data ? (
        links.data.programs.length === 0 ? (
          <p className="muted">No affiliate program mapped to this merchant yet.</p>
        ) : (
          <table className="table compact">
            <thead>
              <tr>
                <th scope="col">Network</th>
                <th scope="col">Program</th>
                <th scope="col">Relationship</th>
                <th scope="col">Commission</th>
                <th scope="col" className="num">Coupon links</th>
                <th scope="col" className="num">Merchant links</th>
                <th scope="col">
                  <span className="visually-hidden">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {links.data.programs.map((program) => (
                <tr key={program.ref} className={program.isActive ? 'row-active' : undefined}>
                  <td>
                    {program.network.name}
                    {!program.network.hasGenerator ? <span className="badge badge-warn">no generator</span> : null}
                  </td>
                  <td>
                    {program.name} <small className="muted">#{program.networkProgramId}</small>
                  </td>
                  <td>
                    <span className={`badge ${program.relationshipStatus === 'approved' ? 'badge-ok' : 'badge-warn'}`}>{program.relationshipStatus}</span>
                  </td>
                  <td>{program.commission ?? '–'}</td>
                  <td className="num">
                    {program.couponLinks.active}/{program.couponLinks.total}
                    {program.couponLinks.failed ? <span className="text-warn"> ({program.couponLinks.failed} failed)</span> : null}
                  </td>
                  <td className="num">
                    {program.merchantLinks.active}/{program.merchantLinks.total}
                  </td>
                  <td className="actions">
                    {program.isActive ? (
                      <span className="badge badge-ok">Active</span>
                    ) : links.data!.canSwitch ? (
                      <button type="button" className="btn btn-small" onClick={() => openPreview(program.ref)}>
                        Switch to this…
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}
      {preview ? (
        <Modal
          title={preview.data ? `Switch ${preview.data.merchant} to ${preview.data.target.network}?` : 'Preparing preview'}
          onClose={() => setPreview(null)}
          actions={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setPreview(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={!preview.data?.allowed || switching} onClick={confirm}>
                {switching ? 'Switching…' : 'Confirm switch'}
              </button>
            </>
          }
        >
          {preview.error ? <p role="alert">{preview.error}</p> : null}
          {!preview.data && !preview.error ? <Loading label="Checking links" /> : null}
          {preview.data ? (
            <div className="switch-preview">
              <p>
                Program <strong>{preview.data.target.program}</strong>. Nothing changes until you confirm.
              </p>
              <ul className="summary-chips">
                <li>{preview.data.summary.generate} to generate</li>
                <li>{preview.data.summary.reactivate} to re-activate</li>
                <li>{preview.data.summary.alreadyActive} already active</li>
                <li className={preview.data.summary.cannotGenerate ? 'warn' : ''}>{preview.data.summary.cannotGenerate} cannot generate</li>
              </ul>
              {preview.data.warnings.length ? (
                <ul className="warnings">
                  {preview.data.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              ) : null}
              {!preview.data.allowed ? <p className="text-warn">You need switch rights on every site that shows this merchant.</p> : null}
              <table className="table compact">
                <thead>
                  <tr>
                    <th scope="col">Coupon</th>
                    <th scope="col">Site</th>
                    <th scope="col">Now on</th>
                    <th scope="col">Would</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.lines.map((line, index) => (
                    <tr key={index}>
                      <td>{line.couponTitle}</td>
                      <td>{line.propertyName}</td>
                      <td>{line.currentNetwork ?? '–'}</td>
                      <td className={line.action === 'cannot_generate' ? 'text-warn' : undefined}>{ACTION_LABELS[line.action]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </article>
  );
}

function MerchantCoupons({ workspace }: { workspace: Workspace }) {
  const { setQuery } = useRouter();
  return (
    <article className="card span-2" aria-labelledby="mc-title">
      <h2 id="mc-title">Coupons ({workspace.coupons.length})</h2>
      <table className="table compact">
        <thead>
          <tr>
            <th scope="col">Title</th>
            <th scope="col">Code</th>
            <th scope="col">Ends</th>
            {workspace.sites.map((site) => (
              <th scope="col" key={site.propertyRef}>
                {site.propertyName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {workspace.coupons.map((coupon) => (
            <tr key={coupon.ref} onClick={() => setQuery({ coupon: coupon.ref })} className="clickable">
              <td>
                <button type="button" className="link-button" onClick={() => setQuery({ coupon: coupon.ref })}>
                  {coupon.title}
                </button>
              </td>
              <td>{coupon.code ? <code>{coupon.code}</code> : <span className="muted">deal</span>}</td>
              <td>{formatDate(coupon.endsAt)}</td>
              {workspace.sites.map((site) => {
                const state = coupon.states[site.propertyRef];
                return (
                  <td key={site.propertyRef}>
                    <StateBadge state={state?.state ?? null} live={state?.live} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </article>
  );
}
